/*
  fp-birthday-reminder.js
  Verjaardags-herinneringspagina. Klant vult tot 3 kinderen/kleinkinderen in
  (voornaam + geboortedatum, jaar optioneel) en de gegevens gaan als
  profile-properties naar Klaviyo via de publieke Client API (company_id
  U6v7ix). Geen private key, geen server-call: rechtstreeks vanuit de browser
  naar https://a.klaviyo.com/client/profiles, zoals bedoeld voor deze
  publiek-bruikbare endpoint.

  Properties per kind (i = 1..3):
  - birthday_{i}_name
  - birthday_{i}_date        (YYYY-MM-DD; jaar onbekend -> 1900)
  - birthday_{i}_year_known  (false als jaar niet is ingevuld)
  - birthday_{i}_remind      (YYYY-MM-DD, 21 dagen vóór de verjaardag). De
    Klaviyo-datumflow start óp deze datum: een flow die "21 dagen vooraf"
    start, wacht daarna tot de datum zelf en mailt pas óp de verjaardag.
  Plus birthday_reminder_signup = ISO-datum van vandaag.
  Lege kind-rijen (geen naam of geen datum) worden niet meegestuurd.
*/
(function () {
  if (customElements.get('fp-birthday-reminder')) return;

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function daysInMonth(month, year) {
    const y = year || 2024; // schrikkeljaar als fallback zodat 29 feb altijd geldig is zonder jaar
    return new Date(y, month, 0).getDate();
  }

  customElements.define(
    'fp-birthday-reminder',
    class FpBirthdayReminder extends HTMLElement {
      connectedCallback() {
        this.companyId = this.dataset.companyId;
        this.apiBase = this.dataset.apiBase || 'https://a.klaviyo.com';
        this.apiRevision = this.dataset.apiRevision || '2026-07-15';
        this.maxChildren = parseInt(this.dataset.maxChildren, 10) || 3;

        this.texts = {
          emailRequired: this.dataset.textEmailRequired,
          emailInvalid: this.dataset.textEmailInvalid,
          nameRequired: this.dataset.textNameRequired,
          dateRequired: this.dataset.textDateRequired,
          dateInvalid: this.dataset.textDateInvalid,
          noneFilled: this.dataset.textNoneFilled,
          submitting: this.dataset.textSubmitting,
          submit: this.dataset.textSubmit,
        };

        this.form = this.querySelector('[data-role="form"]');
        this.emailInput = this.querySelector('[data-role="email-input"]');
        this.emailError = this.querySelector('[data-role="email-error"]');
        this.childrenWrap = this.querySelector('[data-role="children"]');
        this.childTemplate = this.querySelector('[data-role="child-template"]');
        this.addButton = this.querySelector('[data-role="add-child"]');
        this.formError = this.querySelector('[data-role="form-error"]');
        this.submitButton = this.querySelector('[data-role="submit-button"]');
        this.successEl = this.querySelector('[data-role="success"]');
        this.errorEl = this.querySelector('[data-role="error"]');
        this.retryButton = this.querySelector('[data-role="retry-button"]');

        this.childCount = 0;

        this.prefillEmail();
        this.addChild();
        this.addChild();

        this.addButton.addEventListener('click', () => this.addChild());
        this.form.addEventListener('submit', (event) => this.onSubmit(event));
        this.retryButton.addEventListener('click', () => this.showForm());

        this.updateAddButtonVisibility();
      }

      prefillEmail() {
        try {
          const params = new URLSearchParams(window.location.search);
          const email = params.get('e');
          if (email) this.emailInput.value = decodeURIComponent(email);
        } catch (e) {
          /* geen prefill, gewoon leeg veld tonen */
        }
      }

      addChild() {
        if (this.childCount >= this.maxChildren) return;
        this.childCount += 1;
        const index = this.childCount;

        const fragment = this.childTemplate.content.cloneNode(true);
        const fieldset = fragment.querySelector('[data-role="child"]');
        fieldset.dataset.index = String(index);

        const legend = fragment.querySelector('[data-role="child-legend"]');
        legend.textContent = this.formatChildHeading(index);

        const removeBtn = fragment.querySelector('[data-role="remove-child"]');
        removeBtn.hidden = index === 1;
        removeBtn.addEventListener('click', () => this.removeChild(fieldset));

        // Maand-select is al server-side gevuld (vertaalde maandnamen via Liquid);
        // hier alleen de overige elementen aan de state koppelen.
        this.childrenWrap.appendChild(fragment);
        this.updateAddButtonVisibility();
      }

      formatChildHeading(index) {
        // 'general.fp.birthday.child_heading' met {{ number }} is al gerenderd
        // op de knopcopy elders; voor de legend gebruiken we het data-attribuut
        // dat de template zelf meegeeft via textContent van een verborgen bron.
        const template = this.dataset.childHeadingTemplate;
        if (template) return template.replace('__N__', String(index));
        return `#${index}`;
      }

      removeChild(fieldset) {
        fieldset.remove();
        this.childCount -= 1;
        this.renumberChildren();
        this.updateAddButtonVisibility();
      }

      renumberChildren() {
        const fieldsets = Array.from(this.childrenWrap.querySelectorAll('[data-role="child"]'));
        fieldsets.forEach((fieldset, i) => {
          const index = i + 1;
          fieldset.dataset.index = String(index);
          const legend = fieldset.querySelector('[data-role="child-legend"]');
          if (legend) legend.textContent = this.formatChildHeading(index);
          const removeBtn = fieldset.querySelector('[data-role="remove-child"]');
          if (removeBtn) removeBtn.hidden = index === 1;
        });
      }

      updateAddButtonVisibility() {
        this.addButton.hidden = this.childCount >= this.maxChildren;
      }

      clearErrors() {
        this.emailError.hidden = true;
        this.emailInput.classList.remove('fp-birthday-input--invalid');
        this.formError.hidden = true;
        this.querySelectorAll('[data-role="child-error"]').forEach((el) => {
          el.hidden = true;
        });
        this.querySelectorAll('.fp-birthday-input--invalid').forEach((el) => {
          el.classList.remove('fp-birthday-input--invalid');
        });
      }

      setFieldError(el, message) {
        el.hidden = false;
        el.textContent = message;
      }

      validate() {
        this.clearErrors();
        let valid = true;

        const email = this.emailInput.value.trim();
        if (!email) {
          this.setFieldError(this.emailError, this.texts.emailRequired);
          this.emailInput.classList.add('fp-birthday-input--invalid');
          valid = false;
        } else if (!EMAIL_RE.test(email)) {
          this.setFieldError(this.emailError, this.texts.emailInvalid);
          this.emailInput.classList.add('fp-birthday-input--invalid');
          valid = false;
        }

        const children = [];
        const fieldsets = Array.from(this.childrenWrap.querySelectorAll('[data-role="child"]'));

        fieldsets.forEach((fieldset) => {
          const nameInput = fieldset.querySelector('[data-role="name-input"]');
          const dayInput = fieldset.querySelector('[data-role="day-input"]');
          const monthInput = fieldset.querySelector('[data-role="month-input"]');
          const yearInput = fieldset.querySelector('[data-role="year-input"]');
          const errorEl = fieldset.querySelector('[data-role="child-error"]');

          const name = nameInput.value.trim();
          const day = dayInput.value.trim();
          const month = monthInput.value.trim();
          const year = yearInput.value.trim();

          const isEmpty = !name && !day && !month && !year;
          if (isEmpty) return; // lege rij: gewoon overslaan, niet meesturen

          let rowValid = true;

          if (!name) {
            this.setFieldError(errorEl, this.texts.nameRequired);
            nameInput.classList.add('fp-birthday-input--invalid');
            rowValid = false;
          }

          if (!day || !month) {
            if (rowValid) this.setFieldError(errorEl, this.texts.dateRequired);
            if (!day) dayInput.classList.add('fp-birthday-input--invalid');
            if (!month) monthInput.classList.add('fp-birthday-input--invalid');
            rowValid = false;
          } else {
            const dayNum = parseInt(day, 10);
            const monthNum = parseInt(month, 10);
            const yearNum = year ? parseInt(year, 10) : null;
            const maxDay = daysInMonth(monthNum, yearNum);
            const currentYear = new Date().getFullYear();

            const dayOk = dayNum >= 1 && dayNum <= maxDay;
            const monthOk = monthNum >= 1 && monthNum <= 12;
            const yearOk = !year || (yearNum >= 1900 && yearNum <= currentYear);

            if (!dayOk || !monthOk || !yearOk) {
              if (rowValid) this.setFieldError(errorEl, this.texts.dateInvalid);
              if (!dayOk) dayInput.classList.add('fp-birthday-input--invalid');
              if (!monthOk) monthInput.classList.add('fp-birthday-input--invalid');
              if (!yearOk) yearInput.classList.add('fp-birthday-input--invalid');
              rowValid = false;
            }
          }

          if (!rowValid) {
            valid = false;
            return;
          }

          children.push({
            name,
            day: day.padStart(2, '0'),
            month: month.padStart(2, '0'),
            year: year || null,
          });
        });

        if (valid && children.length === 0) {
          this.setFieldError(this.formError, this.texts.noneFilled);
          valid = false;
        }

        return { valid, email, children };
      }

      // Verjaardag min 21 dagen, in UTC gerekend zodat zomertijd niets
      // verschuift. Het jaar doet er voor de jaarlijkse flow niet toe.
      remindDate(year, month, day) {
        const d = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
        d.setUTCDate(d.getUTCDate() - 21);
        return d.toISOString().slice(0, 10);
      }

      buildProperties(email, children) {
        const properties = {};
        children.forEach((child, i) => {
          const n = i + 1;
          const yearKnown = !!child.year;
          const year = yearKnown ? child.year : '1900';
          properties[`birthday_${n}_name`] = child.name;
          properties[`birthday_${n}_date`] = `${year}-${child.month}-${child.day}`;
          properties[`birthday_${n}_year_known`] = yearKnown;
          properties[`birthday_${n}_remind`] = this.remindDate(year, child.month, child.day);
        });
        properties.birthday_reminder_signup = new Date().toISOString().slice(0, 10);
        return properties;
      }

      async onSubmit(event) {
        event.preventDefault();
        const { valid, email, children } = this.validate();
        if (!valid) return;

        this.submitButton.disabled = true;
        this.submitButton.textContent = this.texts.submitting;

        const properties = this.buildProperties(email, children);
        const url = `${this.apiBase}/client/profiles?company_id=${encodeURIComponent(this.companyId)}`;
        const body = {
          data: {
            type: 'profile',
            attributes: {
              email,
              properties,
            },
          },
        };

        try {
          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/vnd.api+json',
              revision: this.apiRevision,
            },
            body: JSON.stringify(body),
          });

          // De Client API geeft 202 Accepted zonder body terug bij succes.
          if (response.ok || response.status === 202) {
            this.showSuccess();
          } else {
            this.showError();
          }
        } catch (e) {
          this.showError();
        } finally {
          this.submitButton.disabled = false;
          this.submitButton.textContent = this.texts.submit;
        }
      }

      showSuccess() {
        this.form.hidden = true;
        this.errorEl.hidden = true;
        this.successEl.hidden = false;
      }

      showError() {
        this.form.hidden = true;
        this.successEl.hidden = true;
        this.errorEl.hidden = false;
      }

      showForm() {
        this.errorEl.hidden = true;
        this.successEl.hidden = true;
        this.form.hidden = false;
      }
    }
  );
})();
